import { createQueryKeyStore } from '@lukemorales/query-key-factory';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { MappingTicketApis } from './mappingTicketApis';
import {
	CreateMemberBulkType,
	GenderType,
	MemberInfoType,
	TicketBaseInfoType,
} from './mappingTicketType';

export type MemberUpdateType = {
	id: number;
	name: string;
	birth: string;
	gender: GenderType;
};

const mappingTicketQuery = createQueryKeyStore({
	mappingTicket: {
		getTicketMappingInfo: (ticketId: string) => ({
			queryKey: [ticketId],
			queryFn: async () => {
				const data = await MappingTicketApis.getTicketMappingInfo({ ticketId });
				return data ?? null;
			},
		}),
		getTicketMappingInfoForOrders: (orderId: string) => ({
			queryKey: [orderId],
			queryFn: async () => {
				const data = await MappingTicketApis.getTicketMappingInfoListForOrder({
					orderIdList: [orderId],
				});
				return data ?? [];
			},
		}),
	},
});

const useCreateMember = ({
	onSuccess,
	...others
}: Omit<
	UseMutationOptions<
		TicketBaseInfoType,
		unknown,
		{
			ticketId: string;
			memberInfo: MemberInfoType;
		},
		unknown
	>,
	'mutationFn'
>) => {
	const refresh = useRefresh();
	return useMutation({
		mutationFn: async (data: { ticketId: string; memberInfo: MemberInfoType }) => {
			return await MappingTicketApis.createMember(data);
		},
		onSuccess: (data, variable, context) => {
			refresh();
			onSuccess && onSuccess(data, variable, context);
		},
		...others,
	});
};

const useCreateMemberBulk = ({
	onSuccess,
	...others
}: Omit<
	UseMutationOptions<TicketBaseInfoType[], unknown, CreateMemberBulkType, unknown>,
	'mutationFn'
>) => {
	const refresh = useRefresh();
	return useMutation({
		mutationFn: async (data: CreateMemberBulkType) => {
			return await MappingTicketApis.createMemberBulk(data);
		},
		onSuccess: (data, variable, context) => {
			refresh();
			onSuccess && onSuccess(data, variable, context);
		},
		...others,
	});
};

const useUpdateMember = ({
	onSuccess,
	...others
}: Omit<
	UseMutationOptions<MemberInfoType, unknown, { memberInfo: MemberInfoType }, unknown>,
	'mutationFn'
>) => {
	const refresh = useRefresh();
	useMutation({
		mutationFn: async (data: { memberInfo: MemberInfoType }) => {
			return await MappingTicketApis.updateMember(data);
		},
		onSuccess: (data, variable, context) => {
			refresh();
			onSuccess && onSuccess(data, variable, context);
		},
		...others,
	});
};

const useUpdateMapping = ({
	onSuccess,
	...others
}: Omit<
	UseMutationOptions<TicketBaseInfoType, unknown, { ticketId: string; memberId: number }, unknown>,
	'mutationFn'
>) => {
	const refresh = useRefresh();
	return useMutation({
		mutationFn: async (data: { ticketId: string; memberId: number }) => {
			return await MappingTicketApis.updateMapping(data);
		},
		onSuccess: (data, variable, context) => {
			refresh();
			onSuccess && onSuccess(data, variable, context);
		},
		...others,
	});
};
const useDeleteMember = ({
	onSuccess,
	...others
}: Omit<UseMutationOptions<unknown, unknown, { memberId: number }, unknown>, 'mutationFn'>) => {
	const refresh = useRefresh();
	return useMutation({
		mutationFn: async (data: { memberId: number }) => {
			return await MappingTicketApis.deleteMember(data);
		},
		onSuccess: (data, variable, context) => {
			refresh();
			onSuccess && onSuccess(data, variable, context);
		},
		...others,
	});
};
const useRefresh = () => {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries({
			queryKey: mappingTicketQuery.mappingTicket._def,
		});
	};
};
export const MappingTicketQuery = {
	query: mappingTicketQuery,
	useRefresh,
	useCreateMember,
	useCreateMemberBulk,
	useUpdateMember,
	useUpdateMapping,
	useDeleteMember,
};
